/** A question field and a stated scheduling target do not assert an observed
 * facility quantity, utility queue duration or financial payback. */
export function isOpenQuestionNumber(text: string, index: number): boolean {
  const lineStart = text.lastIndexOf("\n", Math.max(0, index - 1)) + 1;
  const lineEnd = text.indexOf("\n", index);
  const line = text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
  const relative = index - lineStart;
  const fields = [
    ...line.matchAll(
      /(?:^|\|)\s*(?:Open Question|Unresolved Question)\s*:\s*/gi,
    ),
  ];
  return fields.some((field) => {
    const start = field.index! + field[0].length;
    const end = line.indexOf("|", start);
    const question = line.slice(start, end < 0 ? undefined : end).trim();
    return (
      relative >= start &&
      (end < 0 || relative < end) &&
      /\?\s*$/.test(question) &&
      /^(?:what|which|how|whether|has|have|is|are|does|do|can|could|would|will)\b/i.test(
        question,
      )
    );
  });
}

export function isDeclaredScheduleTarget(
  text: string,
  index: number,
  length: number,
): boolean {
  const before = text.slice(Math.max(0, index - 110), index);
  const after = text.slice(index + length, index + length + 85);
  return (
    /\b(?:desired|target(?:ed)?|requested|planned|declared|(?:operator|owner|developer|customer)(?:['’]s)?)\s+(?:(?:project|completion|energization|construction|timeline|schedule|planning)\s+){0,2}(?:\d+(?:\.\d+)?\s*[-–—]\s*)?$/i.test(
      before,
    ) &&
    /^\s*(?:project\s+)?(?:(?:completion|construction|delivery|energization|development|planning)\s+)?(?:window|timeline(?:\s+target)?|schedule(?:\s+target)?|target(?:\s+window|\s+timeline)?)\b/i.test(
      after,
    )
  );
}

/** An explicitly national/regional generator cohort statistic is not this
 * site's utility queue. This classifies the number's subject; it does not
 * validate the statistic, source attribution, or any inference about load. */
export function isRegionalGeneratorQueueBenchmark(text: string, index: number): boolean {
  const before = text.slice(Math.max(0, index - 230), index).replace(/\*/g, '');
  return /\b(?:national|regional)\s+(?:average\s+)?interconnection\s+queue\s+(?:times?|delays?|durations?)\s+for\s+(?:large\s+)?(?:generators?|generation\s+projects?)\s+(?:(?:is|are|was|were|average|averages|averaged)\s+)?(?:(?:over|above|about|approximately|at\s+least)\s+)?$/i.test(before);
}

/**
 * Whether a duration is FRAMED as a generator-cohort queue benchmark
 * (national / regional / industry / LBNL averages, medians or statistics for
 * generator interconnections).
 *
 * DIAGNOSTIC ONLY — deliberately broader than
 * `isRegionalGeneratorQueueBenchmark` and NEVER an exemption. A benchmark
 * that no cited current source verifies still blocks; this only lets the
 * violation say what is actually wrong ("benchmark not source-verified")
 * instead of accusing the report of inventing this site's queue duration.
 * The only path that clears a benchmark is the source-verified branch in
 * `isSupportedSourceRoleNumber`.
 */
export function isGeneratorQueueBenchmarkFraming(text: string, index: number): boolean {
  const before = text.slice(Math.max(0, index - 230), index).replace(/\*/g, '');
  return /\b(?:national|regional|industry|LBNL|Berkeley\s+Lab)\b[^.;]{0,40}\b(?:benchmarks?|averages?|medians?|statistics?|data|queue\s+(?:times?|delays?|durations?))\b[^.;]{0,40}\b(?:generators?|generation(?:\s+(?:projects?|interconnections?))?|generator\s+interconnections?)\b[^.;]{0,40}$/i.test(before);
}

/** A stated plant size differs from an explicitly hypothetical/attributed option. */
export function isAssertedGenerationCapacity(text: string, index: number, length: number): boolean {
  const tokenLength = /^\d[\d,.]*\s*(?:MW|GW|megawatts?)\b/i.exec(text.slice(index))?.[0].length ?? length;
  const period = text.lastIndexOf('. ', index - 1);
  let start = Math.max(text.lastIndexOf('\n', index - 1) + 1, period < 0 ? 0 : period + 2, text.lastIndexOf('|', index - 1) + 1, 0);
  const tail = text.slice(index + tokenLength);
  const endOffset = tail.search(/[.!?](?=\s|$)|\n|\|/);
  let end = endOffset < 0 ? text.length : index + tokenLength + endOffset;
  // A peer/scenario in one contrast clause cannot qualify the site's next claim.
  const originalStart = start;
  for (const separator of text.slice(start, end).matchAll(/;|\b(?:but|whereas|while)\b/gi)) {
    const at = originalStart + separator.index!;
    if (at < index) start = at + separator[0].length;
    else { end = at; break; }
  }
  const clause = text.slice(start, end), before = text.slice(start, index), after = text.slice(index + tokenLength, end);
  const actorAfter = /^\s+(?:of\s+)?(?:(?:phased|BTM|on[- ]site|gas[- ]fired|gas|power)\s+){0,4}(?:gas\s+plant|power\s+plant|generation(?:\s+(?:plant|facility|capacity))?)\b/i.exec(after)
    ?? /^\s+(?:(?:BTM|on[- ]site|gas[- ]fired|gas)\s+){1,3}plant\b/i.exec(after);
  const actorBefore = /\b(?:gas[- ]plant|power[- ]plant|(?:gas|power)\s+generation)(?:\s+(?:nameplate|generation|capacity|size))*(?:\s+(?:is|of|at|equals?|will\s+be))?\s*[:=]?\s*$/i.test(before);
  if (!actorAfter && !actorBefore) return false;
  const certain = [...clause.matchAll(/\b(?:confirmed|guaranteed|committed|secured|approved|operating|operational)\b/gi)]
    .some(match => !/\b(?:not|never|no|without)\s+(?:(?:yet|currently|a|an|been)\s+){0,2}$/i.test(clause.slice(0, match.index)))
    || /\b(?:this|the|our)\s+(?:project|site|campus|facility)\s+(?:requires?|needs?|will\s+(?:build|install|add)|has)\s+(?:an?\s+)?$/i.test(before);
  const afterPlantRole = actorAfter ? after.slice(actorAfter[0].length) : '';
  const conditionalStudy = /\bif\s+(?:an?\s+)?$/i.test(before)
    && /^\s+(?:were|was)\s+(?:considered|evaluated|studied)\b/i.test(afterPlantRole)
    || /^\s+(?:may|might|could)\s+be\s+(?:considered|evaluated|studied)\b/i.test(afterPlantRole);
  if ((!certain && (conditionalStudy || /\b(?:hypothetical|illustrative|scenario|sensitivity|comparison|compare|comparing|option)\b/i.test(clause)))
    || /\b(?:peer|comparable|other|another)\s+(?:project|site|facility|plant)\b/i.test(clause)
    || /\b(?:not|never|no)\s+(?:an?\s+)?$/i.test(before)) return false;
  const attributedPlan = /\b(?:developer|owner|applicant)(?:['’]s)?\b[^.;|]{0,100}\b(?:propos(?:es?|al)|assum(?:es?|ption)|planning\s+(?:case|target)|targets?|estimates?|suggests?)\b/i.test(before);
  return !attributedPlan || certain;
}

/** A separately named, geographically attributed benchmark does not assert
 * this site's load. Its source/value still belongs to the citation and
 * comparable-evidence checks; this exemption never validates a benchmark. */
export function isOtherProjectCapacityMention(text: string, index: number, length: number): boolean {
  const start = Math.max(text.lastIndexOf('\n', index - 1) + 1, text.lastIndexOf('. ', index - 1) + 2, 0);
  const end = text.indexOf('. ', index + length);
  const context = text.slice(start, end < 0 ? undefined : end);
  const before = text.slice(Math.max(start, index - 110), index);
  const after = text.slice(index + length, index + length + 100);
  return /\b(?:benchmarks?|comparable|similar|peer|other|another)\b/i.test(context)
    && /\b[A-Z][A-Za-z0-9&-]*(?:\s+[A-Z][A-Za-z0-9&-]*){0,4}['’]s\s*$/.test(before)
    && !/\b(?:site|project|campus|facility|development)['’]s\s*$/i.test(before)
    && /^\s+(?:facility|project|campus|data\s+cent(?:er|re))\s+in\s+[A-Z]/.test(after);
}
